import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import FormItem from "@components/FormItem/FormItem";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHeldIntermediateSurveys } from "../hooks/useHeldIntermediateSurveysStore";
import { useGatedOpen } from "../hooks/usePollingGate";
import { useValidationToast } from "../hooks/useValidationToast";
import {
  INTERMEDIATE_SURVEY_FIELDS as FIELDS,
  intermediateSurveySchema,
  L_FDS,
  type IntermediateSurveyFormData,
} from "./closeOutSchemas";

const IntermediateSurveyDialog: React.FC<{
  operationId: string;
  shipperCode: string;
  onClose: () => void;
}> = ({ operationId, shipperCode, onClose }) => {
  const { t } = useTranslation();
  useGatedOpen(true);
  const onInvalid = useValidationToast();
  const held = useHeldIntermediateSurveys(operationId);
  const schema = useMemo(() => intermediateSurveySchema(t), [t]);
  const form = useForm<IntermediateSurveyFormData>({
    resolver: zodResolver(schema),
    defaultValues: { amount: shipperCode in held.amounts ? String(held.amounts[shipperCode]) : "" },
  });

  const submit = form.handleSubmit((values) => {
    held.set(shipperCode, values.amount === "" ? null : Number(values.amount));
    onClose();
  }, onInvalid);

  return (
    <GenericDialog
      open
      testId="intermediate-survey-dialog"
      title={t(`${L_FDS}.intermediate.title`)}
      cancelButtonText={t(`${L_FDS}.intermediate.cancel`)}
      confirmButtonText={t(`${L_FDS}.intermediate.add`)}
      handleCloseDialog={onClose}
      confirmAction={() => void submit()}
      elementContent={
        <div className="flex flex-col gap-3">
          <Typography variant="body2" data-testid="intermediate-survey-for">
            {t(`${L_FDS}.intermediate.for`, { shipper: shipperCode })}
          </Typography>
          <FormProvider {...form}>
            <FormItem<IntermediateSurveyFormData, "amount">
              name={FIELDS.AMOUNT}
              type="text"
              label={t(`${L_FDS}.intermediate.label`)}
              testId="intermediate-survey-amount"
              slotProps={{ htmlInput: { inputMode: "decimal" } }}
            />
          </FormProvider>
        </div>
      }
    />
  );
};

export default IntermediateSurveyDialog;
